import { useEffect, useMemo, useState } from "react";
import { AnalyticsChartCard } from "../ChartComponents";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { useDashboardUI } from "../../context/GlobalContext";
import { buildAnalyticsQueryParams } from "../../helpers/helpers";

function mergeAnalyticsByDate(metrics) {
    const map = {};

    metrics.forEach(({ key, source, valueKey }) => {
        (source || []).forEach(item => {
            if (!map[item.date]) {
                map[item.date] = { date: item.date };
            }
            map[item.date][key] = item[valueKey] || 0;
        });
    });

    return Object.values(map).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );
}

export default function FileGenerationAnalytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    const {
        selectedFacilityFilter,
        selectedStartDateFilter,
        selectedEndDateFilter,
        setFileGenSummary,
    } = useDashboardUI();

    const fetchAnalytics = async () => {
        try {
            setLoading(true);

            const queryParams = buildAnalyticsQueryParams({
                startValue: selectedStartDateFilter,
                endValue: selectedEndDateFilter,
                facilityId: selectedFacilityFilter?.facility_number,
            });

            const res =
                await DashboardMetricsService.getFileGenerationAnalytics(queryParams);

            setAnalytics(res.data);
            const summary = res.data?.usage_summary;
            if (summary) {
                setFileGenSummary({
                    totalFiles: summary.total_files_generated ?? 0,
                    totalImages: summary.images_generated_today ?? 0,
                    maxImagesPerDay: summary.max_images_per_day ?? 0,
                });
            }
        } catch (err) {
            console.error("Failed to fetch file generation analytics", err);
            setAnalytics(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [selectedStartDateFilter, selectedEndDateFilter, selectedFacilityFilter]);

    const fileGenerationChart = useMemo(() => {
        const sourceData = analytics?.files_generated_datewise || [];

        return {
            chartData: mergeAnalyticsByDate([
                {
                    key: "total_files_generated",
                    source: sourceData,
                    valueKey: "total_files_generated",
                },
            ]),
            lines: [
                {
                    dataKey: "total_files_generated",
                    stroke: "#6366F1",
                    name: "Files Generated",
                },
            ],
        };
    }, [analytics]);

    const imageGenerationChart = useMemo(() => {
        const sourceData = analytics?.images_generated_datewise || [];

        return {
            chartData: mergeAnalyticsByDate([
                {
                    key: "total_images_generated",
                    source: sourceData,
                    valueKey: "total_images_generated",
                },
            ]),
            lines: [
                {
                    dataKey: "total_images_generated",
                    stroke: "#22C55E",
                    name: "Images Generated",
                },
            ],
        };
    }, [analytics]);

    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                    Loading file generation analytics…
                </div>
            </div>
        );
    }

    return (
        <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
            <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
                <AnalyticsChartCard
                    title="Image Generation Over Time"
                    subtitle=""
                    {...imageGenerationChart}
                />

                <AnalyticsChartCard
                    title="File Generation Over Time"
                    subtitle=""
                    {...fileGenerationChart}
                />
            </div>
        </div>
    );
}
